import { useCallback, useEffect, useState, type RefObject } from 'react';
import { useStableCallback } from './use_stable_callback.js';

/** O que o preenchimento guiado diz a UM campo: se ele pede a vez (`wanted`, o
 *  anel), se deve abrir agora como continuação de um gesto (`advance`) e a quem
 *  avisar quando a camada dele fechar (`onSettle`). */
export type FieldGuide = {
  readonly wanted: boolean;
  readonly advance: boolean;
  readonly onSettle: () => void;
};

export type GuidedFlowOptions<F extends string, V> = {
  /** Desligado ⇒ sem anel, sem abertura e sem disparo: `answer`/`confirm`
   *  viram no-op (o campo já gravou o valor pelo `onChange` da barra). */
  readonly guided: boolean;
  readonly value: V;
  /** O que falta responder, NA ORDEM em que a fileira pergunta — o primeiro é
   *  o próximo campo. A ordem é regra de cada serviço, não deste hook. */
  readonly pending: (value: V) => readonly F[];
  /** Se o primeiro pendente já nasce aberto (ver `guidedOpensOnMount`). Lido
   *  uma vez, na montagem. */
  readonly opensOnMount: boolean;
  /** O Buscar: onde o foco pousa quando não resta pergunta. */
  readonly submitRef: RefObject<HTMLElement | null>;
  /** O veto que vale JÁ, independente das datas por confirmar (o host travou,
   *  a rota é impossível): o Confirmar não promete buscar. */
  readonly vetoed: boolean;
  /** A régua de "pode buscar" além da completude, avaliada sobre o valor
   *  NOVO no Confirmar — o veto do host e a validade do pedido. */
  readonly canSubmit: (value: V) => boolean;
  readonly onSubmit: (value: V) => void;
};

export type GuidedFlow<F extends string, V> = {
  readonly wanted: F | null;
  readonly advance: F | null;
  /** O guia de um campo; `owns` diz quais pendências ele responde (o período
   *  do carro responde a retirada e a devolução). */
  readonly fieldGuide: (owns: (field: F) => boolean) => FieldGuide;
  /** Confirmar o campo dono de `owns` agora dispara a busca? É o rótulo
   *  "Confirmar e buscar": só esse campo falta, e nada veta. A validade do
   *  valor novo (`canSubmit`) só se sabe no Confirmar. */
  readonly confirmSearches: (owns: (field: F) => boolean) => boolean;
  /** Um campo foi RESPONDIDO por escolha (um lugar): abre o próximo pendente
   *  ou, sem pendência, leva o foco ao Buscar. */
  readonly answer: (next: V) => void;
  /** O Confirmar de um campo: como `answer`, mas sem pendência é a BUSCA. */
  readonly confirm: (next: V) => void;
  /** As props do Buscar (#5085): travado com campo VAZIO, no guia, ele não
   *  fica inerte sem dizer o quê — o clique abre o primeiro de `asks` (por
   *  padrão a fila do guia), a mesma abertura da continuação. Com o veto, sem
   *  campo vazio, fora do guia ou com `asks` vazio (o pedido é inválido além
   *  do que falta: responder o campo não o destravaria), segue `blocked`. */
  readonly searchCta: (
    blocked: boolean,
    submit: () => void,
    asks?: readonly F[],
  ) => { readonly disabled: boolean; readonly onClick: () => void };
};

/**
 * O preenchimento GUIADO das barras de busca: o primeiro campo pendente pede a
 * vez (o anel), responder um campo abre o próximo pendente e, com tudo
 * respondido, o Confirmar busca (#4620) — escolher um lugar só leva o foco ao
 * Buscar, porque a busca sai de um botão, nunca de um valor que mudou (WCAG
 * 3.2.2, falha F36).
 *
 * Responder é sempre um handler EXPLÍCITO do campo (`answer`/`confirm`), e não
 * o fechamento da camada: Escape e clique fora só zeram o pedido de abertura
 * (`onSettle`) e não buscam.
 *
 * A busca recebe o valor NOVO (`onSubmit(next)`) no mesmo handler: o host não
 * depende do valor do render anterior, e não há pedido esperando um efeito. O
 * foco no Buscar é que espera o commit — antes dele o botão ainda está travado
 * pelo valor antigo, e `focus()` num botão desabilitado não pousa.
 *
 * `advance` é um PEDIDO de abertura, não o estado aberto: o campo apontado
 * abre na borda falso→verdadeiro (`useGuidedOpen`) e avisa quando fecha.
 */
export function useGuidedFlow<F extends string, V>(
  options: GuidedFlowOptions<F, V>,
): GuidedFlow<F, V> {
  const { guided, value, pending } = options;
  const wanted = guided ? (pending(value)[0] ?? null) : null;
  const [advance, setAdvance] = useState<F | null>(() => (options.opensOnMount ? wanted : null));
  const focusSubmit = useFocusAfterCommit(options.submitRef);
  /** Vai para o próximo pendente; devolve se não resta nenhum. */
  const land = (next: V): boolean => {
    const field = pending(next)[0] ?? null;
    setAdvance(field);
    if (field === null) focusSubmit();
    return field === null;
  };
  return {
    wanted,
    advance: guided ? advance : null,
    fieldGuide: (owns) => ({
      wanted: wanted !== null && owns(wanted),
      advance: guided && advance !== null && owns(advance),
      onSettle: () => {
        setAdvance(null);
      },
    }),
    confirmSearches: (owns) => guided && !options.vetoed && pending(value).every(owns),
    answer: (next) => {
      if (guided) land(next);
    },
    confirm: (next) => {
      if (!guided) return;
      if (land(next) && options.canSubmit(next)) options.onSubmit(next);
    },
    searchCta: (blocked, submit, asks = pending(value)) => {
      const ask = blocked && guided && !options.vetoed ? (asks[0] ?? null) : null;
      if (ask === null) return { disabled: blocked, onClick: submit };
      return {
        disabled: false,
        onClick: () => {
          setAdvance(ask);
        },
      };
    },
  };
}

/**
 * Foca `ref` DEPOIS do próximo commit: o Buscar que a resposta acabou de
 * destravar ainda está desabilitado no handler (o valor novo não chegou ao
 * DOM), e `focus()` num botão desabilitado não pousa. O pedido é estável.
 */
export function useFocusAfterCommit(ref: RefObject<HTMLElement | null>): () => void {
  const [requests, setRequests] = useState(0);
  useEffect(() => {
    if (requests > 0) ref.current?.focus();
  }, [requests, ref]);
  return useCallback(() => {
    setRequests((n) => n + 1);
  }, []);
}

/**
 * A abertura de um campo com camada própria (popover/folha) sob o guia: abre na
 * BORDA falso→verdadeiro do pedido — estado derivado, não efeito: abrir é
 * resposta ao pedido que chegou neste render, e no seguinte ele já foi
 * consumido — e avisa o guia ao fechar. O `setOpen` é estável: o campo o usa
 * no efeito que fecha a camada quando o host trava a barra (`busy`).
 *
 * `forKey` (a identidade do valor do campo) amarra a abertura ao valor em que
 * ela foi pedida: um valor que chega de FORA — a preferência do perfil, um
 * recente — fecha o campo, como fecha um pick.
 */
export function useGuidedOpen(
  guide: FieldGuide | undefined,
  forKey?: string,
): readonly [open: boolean, setOpen: (open: boolean) => void] {
  const advance = guide?.advance === true;
  const [opened, setOpened] = useState({ open: advance, forKey });
  const [sawAdvance, setSawAdvance] = useState(advance);
  if (sawAdvance !== advance) {
    setSawAdvance(advance);
    if (advance) setOpened({ open: true, forKey });
  }
  const setOpen = useStableCallback((next: boolean) => {
    setOpened({ open: next, forKey });
    if (!next) guide?.onSettle();
  });
  return [opened.open && opened.forKey === forKey, setOpen];
}
